<template>
  <HeroBlogSection
    :title="hero.title"
    :description="hero.description"
    :type="'main'"
    :blog-title="formattedPost.title"
    :blog-description="formattedPost.description"
    :date="formattedPost.date"
    :datetime="formattedPost.datetime"
    :href="formattedPost.href"
    :image="formattedPost.image"
    :author-name="formattedPost.authorName"
    :author-link="formattedPost.authorLink"
    :color="formattedPost.color"
    :category="formattedPost.category"
  />
  <div
    class="mx-auto xs:px-4 sm:px-6 lg:px-28 w-full grid xs:grid-cols-[1fr] lg:grid-cols-[280px_1fr] gap-8 lg:py-18 lg:max-w-8xl"
  >
    <div sticky-container>
      <SidebarBlog data-aos="fade-in" data-aos-once="true" v-sticky />
    </div>
    <div class="mx-auto grid gap-6 xs:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
      <BlogCard
        v-for="{ date, datetime, title, description, tagText, href, image, color, category } in formattedPosts"
        :key="title"
        :date="date"
        :datetime="datetime"
        :title="title"
        :description="description"
        :tagText="tagText"
        :href="href"
        :image="image"
        :color="color"
        :category="category"
        :snug="true"
      />
    </div>
  </div>
  <!--div ref="observerRef" class="observer md:h-6 lg:h-8 border" /-->
</template>

<script lang="ts" setup>
import { useStore } from 'vuex'
import { format as formatDate, parseISO as parseISODate } from 'date-fns'
import { PostsType, PostType } from '@/types/post-types'
import HeroBlogSection from '@/components/hero/HeroBlogSection'
import { CATEGORIES_QUERY, POSTS_QUERY } from '@/groq/queries'
import AOS from 'aos'

const store = useStore()
const route = useRoute()

useCustomServerMeta({
  title: 'Credsy Blog: Medical Licensing, Credentialing and Payors Solutions',
  description:
    'Follow the Credsy blog to learn more news, reading resources and articles about medical licensing, credentialing and payors.',
})

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [{ name: 'Credsy', item: '/' }, { name: 'About us' }],
  }),
])

const hero: {
  title: string
  description: string
  buttonText: string
} = {
  title: `Credsy <span class="line">blog</span>`,
  description:
    'Get the latest updates on news, reading resources and articles about medical licensing, credentialing and payors.',
  buttonText: 'Book a Consultation',
}

const sanity = useSanity()

const { data: postsResult } = useSanityQuery(POSTS_QUERY)
useAsyncData('sanity-categories', async () => {
  const categories = await sanity.fetch(CATEGORIES_QUERY)

  store.commit('setBlogCategories', {
    categories,
  })
})

const postResult = computed(() => postsResult.value?.[0])

const formattedPost = computed<PostType>(() => {
  return {
    image: postResult.value?.mainImage,
    date:
      (postResult.value?._updatedAt &&
        `${formatDate(parseISODate(postResult.value?._updatedAt), 'dd LLL, yyyy')} updated`) ||
      (postResult.value?._createdAt && formatDate(parseISODate(postResult.value?._createdAt), 'dd LLL, yyyy')),
    datetime: postResult.value?._updatedAt || postResult.value?._createdAt,
    authorName: postResult.value?.author?.name,
    authorLink: `/blog/author/${postResult.value?.author?.slug}`,
    title: postResult.value?.title,
    description: postResult.value?.lead,
    href: `/blog/${postResult.value?.slug}`,
    color: postResult.value?.color?.hex,
    category: {
      ...postResult.value?.category,
      color: postResult.value?.category?.color?.hex,
    },
  }
})

const formattedPosts = computed<PostsType>(() =>
  postsResult.value
    ?.map(({ title, lead, mainImage, _createdAt, _updatedAt, slug, color, category }: any) => ({
      image: mainImage,
      date:
        (_updatedAt && `${formatDate(parseISODate(_updatedAt), 'dd LLL, yyyy')} updated`) ||
        (_createdAt && formatDate(parseISODate(_createdAt), 'dd LLL, yyyy')),
      datetime: _updatedAt || _createdAt,
      title,
      description: lead,
      color: color && color?.hex,
      category: {
        _id: category && category._id,
        title: category && category.title,
        slug: category && category.slug,
        color: category?.color && category.color?.hex,
      },
      href: `/blog/${slug}`,
    }))
    ?.filter(({ category }: any) =>
      store.state.selectedBlogCategories.length ? store.state.selectedBlogCategories.includes(category._id) : true
    )
    ?.filter(({ title }: any) =>
      title.toLowerCase().replace(/\s+/g, '').includes(store.state.blogQuery.toLowerCase().replace(/\s+/g, ''))
    )
)

watch(
  formattedPosts,
  () => {
    AOS.refresh()
  },
  { flush: 'post' }
)

store.commit('setPage', { title: 'Credsy Blog', fullPath: route.fullPath })
</script>
